import type { CreditSpend, Credits } from '@cribbage/protocol';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { optionalStorage } from './settingsStore.js';

/**
 * The weekly hint and review meter, as this device last knew it, per player.
 *
 * One credit pays for a hint, or for the first review of a game
 * (`brain/decisions.md`, 2026-10-08). The server holds the count; this store
 * holds its last answer and the spends made here since, so the device counts
 * down on its own between syncs and offline. Going offline never gives
 * unlimited credits: the count left is the server's last answer minus what was
 * spent here, and the week refills only once the server's `resets_at` has
 * passed.
 *
 * Persisted, because the spends must survive a reload until the server has
 * taken them, and the count must survive an offline launch, which has no `/me`.
 * `session/credits.ts` reads and writes it; nothing else should.
 */

/** One player's meter on this device. */
export interface CreditBook {
  /** The server's last answer. A null limit means nobody is counting (The Improver). */
  readonly limit: number | null;
  readonly left: number | null;
  /** When the server's week turns over. */
  readonly resetsAt: string;
  /** Spent here and not yet taken by the server, oldest first. */
  readonly pending: readonly CreditSpend[];
  /** Games reviewed here, whose later review doors are free. Newest last. */
  readonly reviewed: readonly string[];
  /**
   * The Improver's hints in each game against people, newest game last. Kept
   * apart from `pending`, so a spend the server has taken still counts toward
   * its game, and persisted, so a reload does not hand the game fresh hints.
   */
  readonly peopleHints: readonly GameHints[];
}

/** How many hints one game against people has shown. */
export interface GameHints {
  readonly game: string;
  readonly spent: number;
}

/**
 * A player the server has never answered for: a fresh install, offline. It
 * counts as a guest, the smallest allowance, on weeks that turn over at a past
 * Monday's 4:00 a.m. New York time (8:00 UTC in daylight saving), so the
 * device still refills weekly and the spends made here are all that is
 * subtracted.
 */
export const UNKNOWN_BOOK: CreditBook = {
  limit: 3,
  left: 3,
  resetsAt: '2026-10-05T08:00:00.000Z',
  pending: [],
  reviewed: [],
  peopleHints: [],
};

/** How many reviewed games are remembered. A week of play is far fewer. */
const REVIEWED_KEPT = 200;

/** How many games against people keep their hint count. One is all a table reads. */
const PEOPLE_GAMES_KEPT = 50;

/** Hints shown in `game`, a game against people. */
export function peopleHintsSpent(book: CreditBook, game: string): number {
  return book.peopleHints.find((entry) => entry.game === game)?.spent ?? 0;
}

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

const NEW_YORK = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  hourCycle: 'h23',
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
});

/** How far New York's wall clock runs from UTC at `at`: four hours behind in summer, five in winter. */
function newYorkOffset(at: number): number {
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    Number(NEW_YORK.formatToParts(at).find((each) => each.type === type)?.value);
  const wall = Date.UTC(part('year'), part('month') - 1, part('day'), part('hour'), part('minute'));
  return wall - Math.floor(at / 60_000) * 60_000;
}

/**
 * The turnover `weeks` after the one at `resets`. Every turnover is Monday 4:00
 * a.m. in New York, so one across a daylight-saving change is an hour off a
 * plain multiple of seven days.
 */
function turnover(resets: number, weeks: number): number {
  const plain = resets + weeks * WEEK_MS;
  return plain + newYorkOffset(resets) - newYorkOffset(plain);
}

/**
 * The week holding `now`, as its first and last instants on the server's weekly
 * turnover: the week before `resetsAt` while that is still ahead, and a whole
 * number of weeks past it once it has gone by, however many weeks a device has
 * been offline.
 */
export function weekOf(resetsAt: string, now: number): { start: number; end: number } {
  const resets = Date.parse(resetsAt);
  let weeks = Math.floor((now - resets) / WEEK_MS);
  if (turnover(resets, weeks) > now) weeks -= 1;
  else if (turnover(resets, weeks + 1) <= now) weeks += 1;
  return { start: turnover(resets, weeks), end: turnover(resets, weeks + 1) };
}

/**
 * Credits left in the week holding `now`, or null when nobody is counting.
 *
 * Before the server's `resets_at`, it is the server's count less what was
 * spent here this week and not yet sent. After it, the week has turned over on
 * the device, once or many times: the full allowance less what was spent here
 * since the latest turnover. Only spends in games against bots draw on the
 * allowance. Never below zero.
 */
export function creditsLeft(book: CreditBook, now: number): number | null {
  if (book.limit === null) return null;
  const turned = now >= Date.parse(book.resetsAt);
  const base = turned ? book.limit : (book.left ?? book.limit);
  const weekStart = weekOf(book.resetsAt, now).start;
  const spent = book.pending.filter(
    (spend) => spend.opponents === 'bots' && Date.parse(spend.spent_at) >= weekStart,
  ).length;
  return Math.max(0, base - spent);
}

interface CreditState {
  readonly books: Readonly<Record<string, CreditBook>>;
  /** The server's answer for this player. The spends not yet sent stay. */
  adopt(player: string, credits: Credits): void;
  /**
   * A credit spent here, to send when the device is next online. A hint in a
   * game against people also counts toward that game.
   */
  spend(player: string, spend: CreditSpend): void;
  /** The server took these spends; its answer is the count after them. */
  acknowledge(player: string, ids: readonly string[], credits: Credits): void;
  /** A game reviewed here: its later review doors are free. */
  markReviewed(player: string, gameKey: string): void;
}

/** One more hint in `game`, which moves to the newest end. */
function counted(games: readonly GameHints[], game: string): readonly GameHints[] {
  const spent = (games.find((entry) => entry.game === game)?.spent ?? 0) + 1;
  return [...games.filter((entry) => entry.game !== game), { game, spent }].slice(
    -PEOPLE_GAMES_KEPT,
  );
}

/**
 * The book after a server answer, which may have been read before something
 * newer already landed. Within one week and one allowance the server's count
 * only goes down, since spends only add, so the lower count wins. An answer
 * from a week the book has already moved past is stale, and changes nothing:
 * taken as given, it would turn the week back and refill credits already spent.
 */
function fromServer(book: CreditBook, credits: Credits): CreditBook {
  const next = { limit: credits.limit, left: credits.left, resetsAt: credits.resets_at };
  if (Date.parse(next.resetsAt) < Date.parse(book.resetsAt)) return book;
  const sameWeek = next.resetsAt === book.resetsAt && next.limit === book.limit;
  const left =
    sameWeek && next.left !== null && book.left !== null
      ? Math.min(next.left, book.left)
      : next.left;
  return { ...book, ...next, left };
}

export const useCreditStore = create<CreditState>()(
  persist(
    (set) => {
      const update = (player: string, change: (book: CreditBook) => CreditBook): void => {
        set((state) => ({
          books: { ...state.books, [player]: change(state.books[player] ?? UNKNOWN_BOOK) },
        }));
      };
      return {
        books: {},
        /* A `/me` read before a spend that has since been acknowledged, and
           answered late, cannot hand that credit back (`fromServer`). */
        adopt: (player, credits) => {
          update(player, (book) => fromServer(book, credits));
        },
        spend: (player, spend) => {
          update(player, (book) => ({
            ...book,
            pending: [...book.pending, spend],
            peopleHints:
              spend.kind === 'hint' && spend.opponents === 'people'
                ? counted(book.peopleHints, spend.game_key)
                : book.peopleHints,
          }));
        },
        acknowledge: (player, ids, credits) => {
          update(player, (book) => ({
            ...fromServer(book, credits),
            pending: book.pending.filter((spend) => !ids.includes(spend.id)),
          }));
        },
        markReviewed: (player, gameKey) => {
          update(player, (book) =>
            book.reviewed.includes(gameKey)
              ? book
              : { ...book, reviewed: [...book.reviewed, gameKey].slice(-REVIEWED_KEPT) },
          );
        },
      };
    },
    {
      name: 'cribbage.credits',
      version: 2,
      storage: createJSONStorage(() => optionalStorage),
      partialize: (state) => ({ books: state.books }),
      /* Version 1 kept no per-game count against people: every book starts with none. */
      migrate: (persisted, version) => {
        const books = (persisted as { books?: Record<string, CreditBook> } | null)?.books ?? {};
        if (version >= 2) return { books };
        return {
          books: Object.fromEntries(
            Object.entries(books).map(([player, book]) => [player, { ...book, peopleHints: [] }]),
          ),
        };
      },
    },
  ),
);
